import {useState, useEffect} from 'react';
import {useAuth, ServiceResource, ConfigurationData} from './use-auth';
import {AbeautifulandamazingF3ExcelsiorConfigurationLambdaModel} from '@amzn/f3-excelsior-forecast-configuration-lambda';

/**
 * Fetches the tenant configuration for the current business selection without
 * blocking the initial auth/app render. Re-fetches whenever business, country or flow changes.
 */
export function useTenantConfiguration(): {configurationData: ConfigurationData | undefined; isLoading: boolean} {
    const auth = useAuth();
    const [configurationData, setConfigurationData] = useState<ConfigurationData | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);

    const current = auth.authInformation?.current;
    const businessId = current?.businessId;
    const country = current?.country;
    const flow = current?.flow;
    const canViewConfigurations = current?.permissions?.canViewConfigurations;

    useEffect(() => {
        if (!businessId || !country || !flow) {
            setIsLoading(false);
            return;
        }
        if (!canViewConfigurations) {
            setConfigurationData({} as ConfigurationData);
            setIsLoading(false);
            return;
        }
        // Ignore responses from superseded requests so a slower earlier fetch can't overwrite newer config.
        let cancelled = false;
        setIsLoading(true);
        // Clear the previous tenant's config so consumers don't render stale flags during a switch.
        setConfigurationData(undefined);
        const clientConfigs = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ConfigurationView);
        const client = new AbeautifulandamazingF3ExcelsiorConfigurationLambdaModel(clientConfigs);
        client
            .getConfiguration({business: businessId, country: country, flow: flow, groupId: 'forecast'})
            .promise()
            .then((response) => {
                if (!cancelled) {
                    setConfigurationData(JSON.parse(response.configuration ?? '{}') as ConfigurationData);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error('Failed to fetch tenant configuration:', error);
                    setConfigurationData({} as ConfigurationData);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
        // `auth` is intentionally omitted: it's a new object on every auth update, and the fields we need are listed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, country, flow, canViewConfigurations]);

    return {configurationData, isLoading};
}
